import React, { useContext, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";
import { classNames } from "primereact/utils";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import { TextEditor } from "../../../../components/TextEditor/TextEditor";
import { ToastContext } from "../../../../App";
import {
  useLazyGetStationOptionsListQuery,
  useUpdateOptionGroupMutation,
} from "../../../../redux/services/foodStationOptions";
import {
  foodStationApi,
  useGetStationListQuery,
} from "../../../../redux/services/foodStation";
import getSubdomain from "../../../../utils/getSubdomain";

import * as styles from "../MenuItems.module.css";

const LIST_PATH = "/app/manager/menu-items";

function OptionGroupEditPage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const stationId = searchParams.get("station");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: stations } = useGetStationListQuery(getSubdomain());
  const station = useMemo(
    () => stations?.find((s) => String(s.id) === String(stationId)),
    [stations, stationId],
  );
  const isMultiStation = !!station?.is_multi;
  const subStationOptions = station?.sub_stations || [];

  const [fetchStationOptions, { data: stationOptions }] =
    useLazyGetStationOptionsListQuery();

  useEffect(() => {
    if (stationId) {
      fetchStationOptions({ id: stationId, subDomain: getSubdomain() });
    }
  }, [stationId, fetchStationOptions]);

  const data = useMemo(() => stationOptions || [], [stationOptions]);

  const groupEntry = useMemo(
    () => data.find((g) => String(g.group.group_id) === String(id)),
    [data, id],
  );
  const group = groupEntry?.group;

  const groupOptions = data
    .filter((g) => String(g.group.group_id) !== String(id))
    .map((g) => ({
      label: g.group.group_name,
      value: g.group.group_id,
    }));

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      title: "",
      info: "",
      multiple: "Single Choice",
      max_multiple: { value: 2 },
      show_condition: { otherGroupId: null, itemIds: [] },
    },
  });

  const [selectedSubStation, setSelectedSubStation] = useState(null);

  useEffect(() => {
    if (!group) return;
    reset({
      title: group.group_name || "",
      info: group.info || "",
      multiple: group.allow_multiple ? "Multiple Choice" : "Single Choice",
      max_multiple: { value: group.max_multiple ?? 2 },
      show_condition: group.show_condition || {
        otherGroupId: null,
        itemIds: [],
      },
    });
    const match = subStationOptions.find((s) => s.id === group.sub_station_id);
    setSelectedSubStation(match || subStationOptions[0] || null);
  }, [group, subStationOptions.length, reset]);

  const multiple = watch("multiple");
  const showCondition = watch("show_condition");

  const itemsForSelectedGroup =
    data.find((g) => g.group.group_id === showCondition?.otherGroupId)?.items ||
    [];

  const [
    updateOptionGroup,
    { isSuccess: isUpdateSuccess, isError: isUpdateError, error: updateError },
  ] = useUpdateOptionGroupMutation();

  useEffect(() => {
    if (isUpdateSuccess) {
      showToast("success", "Group", "Group updated!");
      dispatch(
        foodStationApi.util.invalidateTags([
          { type: "FoodStations", id: "LIST" },
        ]),
      );
      navigate(LIST_PATH);
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (isUpdateError) {
      showToast(
        "error",
        updateError?.status,
        updateError?.data?.message ||
          (updateError?.data?.errors && "Incorrect data"),
      );
      setIsSubmitting(false);
    }
  }, [isUpdateError]);

  const onSaveEdit = (formData) => {
    if (isSubmitting) return;
    const resolvedSubStationId = isMultiStation
      ? selectedSubStation?.id
      : subStationOptions?.[0]?.id;
    if (!resolvedSubStationId) {
      showToast("error", "Sub-station", "Select sub-station before saving");
      return;
    }

    const showConditionPayload =
      formData.show_condition && formData.show_condition.otherGroupId
        ? {
            otherGroupId: formData.show_condition.otherGroupId,
            itemIds: formData.show_condition.itemIds || [],
          }
        : null;

    updateOptionGroup({
      id,
      data: {
        name: formData.title,
        info: formData.info,
        sub_station_id: resolvedSubStationId,
        allow_multiple: formData.multiple === "Multiple Choice",
        max_multiple:
          formData.multiple === "Multiple Choice"
            ? +formData.max_multiple.value
            : null,
        show_condition: showConditionPayload,
      },
      subDomain: getSubdomain(),
    });
    setIsSubmitting(true);
  };

  const title = group?.group_name
    ? `Edit Option Group — ${group.group_name}`
    : "Edit Option Group";

  return (
    <EntityFormPage
      title={title}
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          disabled={isSubmitting}
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onSaveEdit)}
        />
      }
    >
      <form>
        {isMultiStation && (
          <div className="field">
            <label>Sub-station</label>
            <Dropdown
              value={selectedSubStation}
              onChange={(e) => setSelectedSubStation(e.value)}
              options={subStationOptions}
              optionLabel="name"
              placeholder="Sub-station"
              className={`w-full ${styles.formDropdown}`}
            />
          </div>
        )}

        <Controller
          name="multiple"
          control={control}
          render={({ field }) => (
            <SelectButton
              {...field}
              options={["Single Choice", "Multiple Choice"]}
              allowEmpty={false}
              className={`mb-5 ${styles.selectButton}`}
            />
          )}
        />

        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Title</label>
              <InputText
                {...field}
                placeholder="Title"
                className={`w-full ${styles.input}`}
              />
            </div>
          )}
        />

        <div className="field">
          <label>Show condition (optional)</label>
          <div className={styles.showConditionWrapper}>
            <Controller
              name="show_condition.otherGroupId"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  options={groupOptions}
                  placeholder="Other option group"
                  className={classNames(
                    styles.input,
                    styles.optionGroupDropdown,
                  )}
                  onChange={(e) => {
                    field.onChange(e.value);
                    setValue("show_condition.itemIds", []);
                  }}
                />
              )}
            />

            <Controller
              name="show_condition.itemIds"
              control={control}
              defaultValue={[]}
              render={({ field }) => {
                const options = (itemsForSelectedGroup || []).map((it) => ({
                  label: it.title || it.name || it.item_title || `${it.id}`,
                  value: it.id,
                }));
                return (
                  <MultiSelect
                    {...field}
                    options={options}
                    placeholder="Items from selected group"
                    className={`w-full ${styles.menuItemsMultiSelect}`}
                    onChange={(e) => field.onChange(e.value)}
                    display="chip"
                  />
                );
              }}
            />
          </div>
        </div>

        {multiple === "Multiple Choice" && (
          <Controller
            name="max_multiple"
            control={control}
            rules={{
              validate: (e) => {
                if (e.value === null) return "This field is required";
                if (e.value < 2) return "Must be 2 or more";
              },
            }}
            defaultValue={{ value: 2 }}
            render={({ field, fieldState }) => (
              <div className="field mt-5">
                <label>Max choice</label>
                <InputNumber
                  {...field}
                  className={`w-full ${styles.numberInput}`}
                  value={field.value.value}
                  onChange={(e) => field.onChange({ value: e.value })}
                />
                {fieldState.error && (
                  <small className="p-error">{fieldState.error.message}</small>
                )}
              </div>
            )}
          />
        )}
      </form>

      <Controller
        name="info"
        control={control}
        render={({ field }) => (
          <div className="field">
            <label>Info</label>
            <TextEditor
              value={field.value}
              onTextChange={(e) => field.onChange(e.htmlValue)}
            />
          </div>
        )}
      />
    </EntityFormPage>
  );
}

export default OptionGroupEditPage;
